import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import helperService from '../services/helperService';
import { useToast } from '../contexts/ToastContext';
import './Profile.css';

const US_STATES = [
  ['AL','Alabama'],['AK','Alaska'],['AZ','Arizona'],['AR','Arkansas'],['CA','California'],
  ['CO','Colorado'],['CT','Connecticut'],['DE','Delaware'],['DC','District Of Columbia'],
  ['FL','Florida'],['GA','Georgia'],['HI','Hawaii'],['ID','Idaho'],['IL','Illinois'],
  ['IN','Indiana'],['IA','Iowa'],['KS','Kansas'],['KY','Kentucky'],['LA','Louisiana'],
  ['ME','Maine'],['MD','Maryland'],['MA','Massachusetts'],['MI','Michigan'],['MN','Minnesota'],
  ['MS','Mississippi'],['MO','Missouri'],['MT','Montana'],['NE','Nebraska'],['NV','Nevada'],
  ['NH','New Hampshire'],['NJ','New Jersey'],['NM','New Mexico'],['NY','New York'],
  ['NC','North Carolina'],['ND','North Dakota'],['OH','Ohio'],['OK','Oklahoma'],['OR','Oregon'],
  ['PA','Pennsylvania'],['RI','Rhode Island'],['SC','South Carolina'],['SD','South Dakota'],
  ['TN','Tennessee'],['TX','Texas'],['UT','Utah'],['VT','Vermont'],['VA','Virginia'],
  ['WA','Washington'],['WV','West Virginia'],['WI','Wisconsin'],['WY','Wyoming'],
];

function Profile() {
  const { user, refreshUser } = useAuth();
  const navigate             = useNavigate();
  const { addToast }         = useToast();

  const [formData, setFormData] = useState({ name:'', phone:'', street:'', city:'', state:'', zip:'', country:'' });
  const [equipmentCatalog, setEquipmentCatalog] = useState([]);
  const [skillsCatalog,    setSkillsCatalog]    = useState([]);
  const [equipment,        setEquipment]        = useState([]);
  const [skills,           setSkills]           = useState([]);
  const [profileExists,    setProfileExists]    = useState(false);
  const [catalogLoading,   setCatalogLoading]   = useState(true);
  const [isSaving,         setIsSaving]         = useState(false);

  // Seed personal info from auth context
  useEffect(() => {
    if (user) {
      setFormData({
        name:    user.name    || '',
        phone:   user.phone   || '',
        street:  user.street  || '',
        city:    user.city    || '',
        state:   user.state   || '',
        zip:     user.zip     || '',
        country: user.country || '',
      });
    }
  }, [user]);

  // Fetch catalogs + existing helper profile in parallel
  useEffect(() => {
    Promise.all([
      helperService.getEquipmentCatalog(),
      helperService.getSkillsCatalog(),
      helperService.getProfile(),
    ]).then(([eqCat, skCat, profile]) => {
      setEquipmentCatalog(eqCat);
      setSkillsCatalog(skCat);
      if (!profile) return;
      setProfileExists(true);
      if (profile.equipment?.length) setEquipment(profile.equipment.map(e => e.id));
      if (profile.skills?.length)    setSkills(profile.skills.map(s => s.id));
    }).finally(() => setCatalogLoading(false));
  }, []);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const toggleId = (setList, id) =>
    setList(prev => prev.includes(id) ? prev.filter(i => i !== id) : [...prev, id]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSaving(true);
    try {
      if (profileExists) {
        await helperService.updateProfile(equipment, skills, {}, formData);
      } else {
        await helperService.saveProfile(equipment, skills, {}, formData);
      }
      setProfileExists(true);
      // Re-fetch user so geocoded location from the address update is reflected in context.
      await refreshUser();
      addToast('Profile saved!', 'success');
    } catch (err) {
      addToast(err.message || 'Could not save profile. Please try again.', 'error');
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <div className="profile-page">
      <div className="profile-container">
        <div className="profile-header">
          <h1>Your Profile</h1>
          <p>Update your personal information and helper capabilities.</p>
        </div>

        <form onSubmit={handleSubmit} className="profile-form">

          {/* ── Personal Information ─────────────────── */}
          <section className="profile-section">
            <h2>Personal Information</h2>

            <div className="form-group">
              <label htmlFor="name">Full Name</label>
              <input type="text" id="name" name="name" value={formData.name}
                onChange={handleChange} placeholder="Your full name" required />
            </div>
            <div className="form-group">
              <label>Email Address</label>
              <input type="email" value={user?.email || ''} readOnly />
            </div>
            <div className="form-group">
              <label htmlFor="phone">Phone Number</label>
              <input type="tel" id="phone" name="phone" value={formData.phone}
                onChange={handleChange} placeholder="Your phone number" />
            </div>
            <div className="form-group">
              <label htmlFor="street">Street Address</label>
              <input type="text" id="street" name="street" value={formData.street}
                onChange={handleChange} placeholder="Street address" />
            </div>
            <div className="form-row">
              <div className="form-group">
                <label htmlFor="city">City</label>
                <input type="text" id="city" name="city" value={formData.city}
                  onChange={handleChange} placeholder="City" />
              </div>
              <div className="form-group">
                <label htmlFor="state">State</label>
                <select id="state" name="state" value={formData.state} onChange={handleChange}>
                  <option value="">Select state…</option>
                  {US_STATES.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
                </select>
              </div>
            </div>
            <div className="form-row">
              <div className="form-group">
                <label htmlFor="zip">Zip Code</label>
                <input type="text" id="zip" name="zip" value={formData.zip}
                  onChange={handleChange} placeholder="Zip code" />
              </div>
              <div className="form-group">
                <label htmlFor="country">Country</label>
                <input type="text" id="country" name="country" value={formData.country}
                  onChange={handleChange} placeholder="Country" />
              </div>
            </div>
          </section>

          {/* ── Equipment ───────────────────────────── */}
          <section className="profile-section">
            <h2>Equipment</h2>
            <p className="section-hint">Select everything you own that could help in an emergency.</p>
            <div className="chip-grid">
              {catalogLoading ? <p className="section-hint">Loading…</p>
                : equipmentCatalog.map(item => (
                  <button key={item.id} type="button"
                    className={`chip${equipment.includes(item.id) ? ' selected' : ''}`}
                    onClick={() => toggleId(setEquipment, item.id)}>
                    {item.label}
                  </button>
                ))
              }
            </div>
          </section>

          {/* ── Skills ──────────────────────────────── */}
          <section className="profile-section">
            <h2>Skills &amp; Certifications</h2>
            <p className="section-hint">Select any skills or certifications you hold.</p>
            <div className="chip-grid">
              {catalogLoading ? <p className="section-hint">Loading…</p>
                : skillsCatalog.map(item => (
                  <button key={item.id} type="button"
                    className={`chip${skills.includes(item.id) ? ' selected' : ''}`}
                    onClick={() => toggleId(setSkills, item.id)}>
                    {item.label}
                  </button>
                ))
              }
            </div>
          </section>

          <div className="form-actions">
            <button type="submit" className="btn-save" disabled={isSaving}>
              {isSaving ? 'Saving…' : 'Save Profile'}
            </button>
            <button type="button" className="btn-skip" disabled={isSaving}
              onClick={() => navigate('/dashboard')}>
              Cancel
            </button>
          </div>

        </form>
      </div>
    </div>
  );
}

export default Profile;
